import type { FieldPath, Fields } from '../../@types/field-path.d';
import { type Attachment } from '../attachments';
import { type Comment } from '../comments';
import { type Item } from '../items';
import { type User } from '../users';

export type SpotcheckEntryStatus = 'unchecked' | 'checked_scanner' | 'checked_manual' | 'unexpected';

export interface SpotcheckSearchEntry {
	docId: string;
	status: SpotcheckEntryStatus;
	quantity?: number;
	totalQuantity?: number;
	kind?: string;
}

export interface SearchSpotcheckEntriesByCodesResult {
	code: string;
	docs: Item[];
	entries: SpotcheckSearchEntry[];
}

export type SearchSpotcheckEntriesByCodesResponse = SearchSpotcheckEntriesByCodesResult[];

export interface SpotcheckScanItemInput {
	item: string;
	quantity: number;
}

export enum SpotcheckStatus {
	OPEN = 'open',
	CLOSED = 'closed',
	CANCELLED = 'cancelled',
}

export type SpotcheckKind = 'order' | 'reservation' | 'kit' | 'custody' | 'item_filter';

export interface Spotcheck {
	_id: string;
	name: string;
	created: string;
	modified: string;
	by: string;
	closed?: string;
	archived?: string;
	docId: string | null;
	kind: SpotcheckKind;
	message: string;
	numChecked: number;
	numTotal: number;
	numUnchecked: number;
	numUnexpected: number;
	numIssues: number;
	outcome?: 'success' | 'issues';
	status: SpotcheckStatus;
	summary: string;
	users: string[];
	assignees?: User[];
	lastAddress: string;
	comments: Comment[];
	attachments: Attachment[];
	itemFilter: {
		location__in?: string[];
		category__in?: string[];
		[key: string]: unknown;
	};
	participants: User[];
}

export type Field =
	| 'name'
	| 'created'
	| 'modified'
	| 'closed'
	| 'progress'
	| 'participants'
	| 'assignees'
	| 'lastAddress'
	| 'summary'
	| 'numIssues';

export const SPOTCHECK_SYSTEM_FIELDS = [
	{
		id: 'name',
		name: 'Name',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'created',
		name: 'Started',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'closed',
		name: 'Completed',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'modified',
		name: 'Last modified',
		kind: 'datetime',
		editor: 'date',
		unit: null,
		basicField: true,
	},
	{
		id: 'progress',
		name: 'Progress',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'participants',
		name: 'Participants',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'assignees',
		name: 'Assignees',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'lastAddress',
		name: 'Last location',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'summary',
		name: 'Description',
		kind: 'string',
		editor: 'text',
		unit: null,
		basicField: true,
		excludeFromFilters: true,
	},
	{
		id: 'numIssues',
		name: 'Issues',
		kind: 'int',
		editor: 'number',
		unit: null,
		basicField: true,
	},
];

export type SpotcheckQueryField = FieldPath<Spotcheck> | keyof Spotcheck;
export type SpotcheckQueryData<F extends SpotcheckQueryField = SpotcheckQueryField> = Fields<
	Spotcheck,
	Extract<F, FieldPath<Spotcheck> | keyof Spotcheck>
>;

/** Default fields for single-spotcheck fetch. */
export const SPOTCHECK_FIELDS = [
	'_id',
	'name',
	'summary',
	'created',
	'modified',
	'closed',
	'archived',
	'status',
	'outcome',
	'kind',
	'docId',
	'itemFilter',
	'by',
	'users',
	'lastAddress',
	'numChecked',
	'numTotal',
	'numUnchecked',
	'numUnexpected',
	'numIssues',
	'participants._id',
	'participants.name',
	'participants.picture',
	'participants.picture_url',
	'participants.status',
	'assignees._id',
	'assignees.name',
	'assignees.picture',
	'assignees.picture_url',
	'assignees.status',
	'comments.*',
	'comments.by.name',
	'comments.by.picture',
	'comments.by.status',
	'comments.by.customer',
	'attachments.*',
	'attachments.by.name',
	'attachments.by.picture',
	'attachments.by.status',
	'attachments.by.customer',
] as const satisfies readonly SpotcheckQueryField[];
